import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import * as moment from 'moment';
import Decimal from 'decimal.js';
import { HttpService } from '../service/http.service';
import { BookingService } from '../service/booking.service';
import { ToastMessageService } from '../toast-message.service';
import { BookingPreConfirmationService } from '../service/booking-pre-confirmation.service';
import { BookingTransactionsService } from '../service/booking-transactions.service';
import { SchoolInvoiceService } from '../school-invoice.service';
import { PaymentReceivedService } from '../service/payment-received.service';
import { PaymentToSupplierService } from '../service/payment-to-supplier.service';
import { PaymentToSupplier } from '../payment-to-supplier';
import { SchoolInvoiceWithPaymentOrders } from '../school-invoice-with-payment-orders';
import { PaymentReceived } from '../payment-received';
import { CurrencyService } from '../service/currency.service';
import { SchoolInvoice } from '../school-invoice';
import { VivawalletPaymentOrderInfo } from '../vivawallet-payment-order-info';
import { ApiError } from '../api-error';
import { ConfirmationService } from 'primeng/api';

@Component({
  selector: 'app-booking-transactions',
  templateUrl: './booking-transactions.component.html',
  styleUrls: ['./booking-transactions.component.css']
})
export class BookingTransactionsComponent implements OnInit {

  bookingDatesForm!: FormGroup;
  paymentReceivedForm: FormArray;
  selectedInvoice!: SchoolInvoice | null;
  paymentOrders: VivawalletPaymentOrderInfo[] = [];
  showVivawalletOrdersView = false;
  salesAgentId: number | null = null;
  canShowPaymentOrders = false;
  hasMultiplePaymentOrders = false;

  constructor(
    private builder: FormBuilder,
    public bookingService: BookingService,
    private httpService: HttpService,
    private toastService: ToastMessageService,
    public bookingPreConfirmationService: BookingPreConfirmationService,
    public bookingTransactionsService: BookingTransactionsService,
    public schoolInvoiceService: SchoolInvoiceService,
    public paymentReceivedService: PaymentReceivedService,
    public paymentToSupplierService: PaymentToSupplierService,
    private confirmationService: ConfirmationService,
    private currencyService: CurrencyService
  ) {
    this.paymentReceivedForm = builder.array([]);
   }

  ngOnInit(): void {
    let booking = this.bookingService.booking;
    this.verifySchoolInvoices();
    if(this.bookingService.user){
        this.bookingTransactionsService.updateBookingFinancialState(booking.id!, booking.preConfirmation!.baseCurrency.id);
    }

    if (this.paymentReceivedService.payments && this.schoolInvoiceService.invoices && this.paymentToSupplierService.payments) {
      this.initPaymentReceivedForm();
      this.initPaymentToSupplierForm();
      this.bookingDatesForm = this.builder.group({
        sentToSchool: booking.sentToSchool == null ? null : booking.sentToSchool.toDate(),
        schoolConfirmed: booking.schoolConfirmed == null ? null : booking.schoolConfirmed.toDate(),
        preConfirmationSent: booking.preConfirmationSent == null ? null : booking.preConfirmationSent.toDate(),
        insuranceSent: booking.insuranceSent == null ? null : booking.insuranceSent.toDate()
      });
    }
    this.httpService.getVivawalletPaymentOrders(booking.id!).subscribe(res => {
      this.paymentOrders = res;
    });
  }

  verifySchoolInvoices(){
    for(let invoice of this.schoolInvoiceService.invoices){
      if(invoice.paymentOrders.length > 1){
        this.hasMultiplePaymentOrders = true;
      }
    }
  }

  updateBookingDates() {
    this.bookingService.booking.sentToSchool = this.bookingDatesForm.value.sentToSchool == null ? null : moment(this.bookingDatesForm.value.sentToSchool).utc(true);
    this.bookingService.booking.schoolConfirmed = this.bookingDatesForm.value.schoolConfirmed == null ? null : moment(this.bookingDatesForm.value.schoolConfirmed).utc(true);
    this.bookingService.booking.preConfirmationSent = this.bookingDatesForm.value.preConfirmationSent == null ? null : moment(this.bookingDatesForm.value.preConfirmationSent).utc(true);
    this.bookingService.booking.insuranceSent = this.bookingDatesForm.value.insuranceSent == null ? null : moment(this.bookingDatesForm.value.insuranceSent).utc(true);
    this.bookingService.booking.startDate = this.bookingService.booking.startDate.utc(true);
    this.bookingService.booking.endDate = this.bookingService.booking.endDate.utc(true);
    this.httpService.putBooking(this.bookingService.booking).subscribe(() => {
      this.toastService.addSuccess("Success", "Dates updated");
      this.bookingService.sendingUpdateRequest = false;
    }, error => {
      let apiError = new ApiError(error);
      this.toastService.addError("Error", apiError.message);
      this.bookingService.sendingUpdateRequest = false;
    })
  }

  updatePaymentsToSupplier() {
    this.httpService.getPaymentsToSupplierByBookingId(this.bookingService.booking.id!).subscribe(res => {
      this.paymentToSupplierService.payments = res;
      this.convertPaymentToSupplier();
      this.bookingTransactionsService.updateBookingFinancialState(this.bookingService.booking.id!, this.bookingService.booking.preConfirmation!.baseCurrency.id);
    });
  }

  removePaymentToSupplier(payment: PaymentToSupplier) {
    this.confirmationService.confirm({
      message: "Are you sur to delete this payment: " + payment.amount + payment.currency.isoCode + "?",
      accept: () => {
        this.httpService.deletepaymentToSupplier(payment.id!).subscribe(() => {
          this.toastService.addSuccess("Success", "Payment is successfully deleted");
          this.bookingTransactionsService.updateTransactions();
        }, error => {
          this.toastService.addError("Error", error.message);
        });
      }
    });
  }

  removeSchoolInvoice(invoice: SchoolInvoiceWithPaymentOrders) {
    this.confirmationService.confirm({
      message: "Are you sur to delete this invoice: " + invoice.invoiceNumber + "?",
      accept: () => {
        this.httpService.deleteSchoolInvoice(invoice.id!).subscribe(() => {
          this.toastService.addSuccess("Success", "Invoice is successfully deleted");
          this.bookingTransactionsService.updateTransactions();
        }, error => {
          this.toastService.addError("Error", error.message);
        });
      }
    });
  }

  editPaymentReceived(payment: PaymentReceived) {
    this.paymentReceivedService.selectedPayemnt = this.extractPaymentReceivedById(payment.id!)!;
    this.bookingTransactionsService.editingPaymentReceived = true;
  }

  extractPaymentReceivedById(paymentId: number) {
    let payments = this.paymentReceivedService.payments;
    for (let payment of payments) {
      if (payment.id == paymentId) {
        return payment;
      }
    }
    return null;
  }

  editPaymentToSupplier(payment: PaymentToSupplier) {
    this.paymentToSupplierService.selectedPayemnt = this.extractPaymentToSupplierById(payment.id!)!;
    this.bookingTransactionsService.editingPaymentToSupplier = true;
  }

  removePaymentReceived(payment: PaymentReceived) {
    this.confirmationService.confirm({
      message: "Are you sur to delete this payment: " + payment.amount + " " + payment.currency.isoCode + "?",
      accept: () => {
        this.httpService.deletePaymentReceived(payment.id!).subscribe(() => {
          this.toastService.addSuccess("Success", "Payment is successfully deleted");
          this.bookingTransactionsService.updatePaymentsReceived();
          this.bookingTransactionsService.updateBookingFinancialState(this.bookingService.booking.id!, this.bookingService.booking.preConfirmation!.baseCurrency.id);
        }, error => {
          this.toastService.addError("Error", error.message);
        });
      }
    });
  }

  extractPaymentToSupplierById(paymentId: number) {
    let payments = this.paymentToSupplierService.payments;
    for (let payment of payments) {
      if (payment.id == paymentId) {
        return payment;
      }
    }
    return null;
  }

  insertPaymentToSupplierForm() {
    this.bookingTransactionsService.paymentToSupplierForm.controls.push(this.builder.group({
      comment: "",
      date: new Date(),
      amount: new Decimal(0),
      paymentType: this.bookingTransactionsService.paymentTypes[0],
      invoiceNumber: "",
      currency: this.currencyService.currencies[0],
      created: null,
      createdBy: null,
      lastModified: null,
      lastModifiedBy: null,
      id: null,
      sortOrder: null,
      activeMenu: false,
      amountInEuro: new Decimal(0),
      paidOrder: null
    }));
  }

  addSchoolInvoice(event: any) {
    this.bookingTransactionsService.updateSchoolInvoices();
    this.bookingTransactionsService.creatingSchoolInvoice = false;
    this.bookingTransactionsService.editingSchoolInvoice = false;
  }

  updateSchoolPayments() {
    this.bookingTransactionsService.creatingPaymentToSupplier = false;
    this.bookingTransactionsService.editingPaymentToSupplier = false;
    this.updatePaymentsToSupplier();
  }

  putPaymentsToSchool() {
    if (this.bookingTransactionsService.paymentsToSupplierToDelete.length > 0) {
      for (let payment of this.bookingTransactionsService.paymentsToSupplierToDelete) {
        this.httpService.deletepaymentToSupplier(payment.id!).subscribe(() => {
          this.bookingTransactionsService.paymentsToSupplierToDelete = this.arrayRemove(this.bookingTransactionsService.paymentsToSupplierToDelete, payment);
        });
      }
    }
    if (this.bookingTransactionsService.paymentToSupplierForm.controls.length == 0) {
      return;
    }
    let paymentList: PaymentToSupplier[] = [];
    let index = 0;
    for (let paymentForm of this.bookingTransactionsService.paymentToSupplierForm.controls) {
      paymentList.push(
        new PaymentToSupplier(
          paymentForm.value.comment,
          moment(paymentForm.value.date).utc(true),
          paymentForm.value.amount,
          paymentForm.value.paymentType,
          paymentForm.value.invoiceNumber,
          paymentForm.value.currency,
          paymentForm.value.paidOrder,
          null,
          paymentForm.value.created,
          paymentForm.value.createdBy,
          paymentForm.value.lastModified,
          paymentForm.value.lastModifiedBy,
          paymentForm.value.id,
          index
        ));
      index++;
    }
  }

  arrayRemove(array: any[], value: any) {
    return array.filter(element => {
      return element != value;
    });
  }

  convertPaymentToSupplier() {
    for (let paymentToSupplier of this.paymentToSupplierService.payments) {
      if (paymentToSupplier.created) {
        paymentToSupplier.created = moment(paymentToSupplier.created);
      }
      paymentToSupplier.hidden = true;
    }
  }

  editSchoolInvoice(invoice: SchoolInvoice) {
    this.bookingTransactionsService.editingSchoolInvoice = true;
    this.schoolInvoiceService.selectedInvoiceId = invoice.id!;
  }

  addPaymentToSupplier() {
    if (this.schoolInvoiceService.invoices.length > 0) {
      this.bookingTransactionsService.creatingPaymentToSupplier = true;
    } else {
      this.toastService.addError("Warning", "This booking has no invoice to pay.");
    }
  }

  addPaymentReceived() {
    this.bookingTransactionsService.creatingPaymentReceived = true;
  }

  initPaymentToSupplierForm() {
    for (let payment of this.paymentToSupplierService.payments) {
      this.bookingTransactionsService.paymentToSupplierForm.controls.push(this.builder.group({
        comment: payment.comment,
        date: moment(payment.date).toDate(),
        amount: payment.amount.toFixed(2),
        paymentType: payment.paymentType,
        invoiceNumber: payment.invoiceNumber,
        currency: payment.currency,
        created: moment(payment.created),
        createdBy: payment.createdBy,
        lastModified: payment.lastModified,
        lastModifiedBy: payment.lastModifiedBy,
        id: payment.id,
        sortOrder: payment.sortOrder,
        activeMenu: false,
        amountInEuro: payment.amountInEuro,
        paidOrder: payment.paidOrder
      }));
    }
  }

  createtSchoolInvoice() {
    this.schoolInvoiceService.invoiceWithPaymentOrders = null;
    this.bookingTransactionsService.creatingSchoolInvoice = true;
    this.schoolInvoiceService.index = null;
  }

  mouseoverPaymentReceived(payment: PaymentReceived) {
    payment.hidden = false;
  }
  mouseoutPaymentReceived(payment: PaymentReceived) {
    payment.hidden = true;
  }

  mouseoverPaymentToSupplier(payment: PaymentToSupplier) {
    payment.hidden = false;
  }
  mouseoutPaymentToSupplier(payment: PaymentToSupplier) {
    payment.hidden = true;
  }

  mouseoverSchoolInvoice(invoice: SchoolInvoiceWithPaymentOrders) {
    invoice.hidden = false;
  }
  mouseoutSchoolInvoice(invoice: SchoolInvoiceWithPaymentOrders) {
    invoice.hidden = true;
  }

  initPaymentReceivedForm() {
    for (let payment of this.paymentReceivedService.payments) {
      this.paymentReceivedForm.controls.push(this.builder.group({
        id: payment.id,
        comment: payment.comment,
        date: moment(payment.date).toDate(),
        paymentType: payment.paymentType,
        amount: payment.amount.toFixed(2),
        currency: payment.currency,
        invoiceNumber: payment.invoiceNumber,
        created: moment(payment.created),
        createdBy: payment.createdBy,
        lastModified: payment.lastModified,
        lastModifiedBy: payment.lastModifiedBy,
        sortOrder: payment.sortOrder,
        activeMenu: false,
        amountInEuro: payment.amountInEuro
      }));
    }
  }

  activateInvoiceFileUpload(invoice: SchoolInvoice) {
    console.log(invoice);
    this.selectedInvoice = invoice;
    this.bookingTransactionsService.creatingSchoolInvoiceFile = true;
  }

  updateFileUploader(event: any) {
    this.bookingTransactionsService.creatingSchoolInvoiceFile = false;
    this.selectedInvoice = null;
  }

  downloadInvoiceFile(invoice: SchoolInvoice) {
    if (invoice.fileId) {
      this.httpService.getSchoolInvoiceFileById(invoice.fileId).subscribe(res => {
        let binaryData: Blob[] = [];
        binaryData.push(res);
        this.downloadFile(binaryData, invoice.fileName!);
      });
    }
  }

  downloadFile(bytes: BlobPart[], fileName: string) {
    let downloadLink = document.createElement('a');
    downloadLink.href = window.URL.createObjectURL(new Blob(bytes, { type: 'blob' }));
    downloadLink.setAttribute('download', fileName);
    document.body.appendChild(downloadLink);
    downloadLink.click();
  }

  activateVivawalletOrdersView(){
    this.showVivawalletOrdersView = true;
  }

  showPaymentOrders(){
    if(this.canShowPaymentOrders){
      this.canShowPaymentOrders = false;
    } else {
      this.canShowPaymentOrders = true;
    }
  }
}
